"use client";

import React, { useState, useEffect, Fragment } from "react";
import Filters from "@/components/store/Filters";
import ProductCard from "@/components/store/ProductCard";
import StoreVideoBanner from "@/components/store/StoreVideoBanner";
import { generateJWTToken } from "@/utils/helpers/helperFunctions";
import { getStoreDisplayPrice } from "@/utils/helpers/storeProductPrice";

const Store = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    currency: "ALL",
    sort: "New Arrivals",
    name: "",
    rarity: [],
    category: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const ITEMS_PER_PAGE = 12;
  
  // Calculate total pages
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  
  // Get current page items
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredProducts.slice(startIndex, endIndex);
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchProducts = async () => {
      // Check if we have cached data
      const cachedData = sessionStorage.getItem('productsData');
      const cacheTimestamp = sessionStorage.getItem('productsTimestamp');
      
      // If we have cached data and it's less than 5 minutes old, use it
      if (cachedData && cacheTimestamp && Date.now() - cacheTimestamp < 300000) {
        const parsedData = JSON.parse(cachedData);
        // Filter out products that are for development only if in production
        const isProduction = process.env.NEXT_PUBLIC_IS_PRODUCTION === 'true';
        const filteredData = isProduction 
          ? parsedData.filter(nft => !nft.is_development) 
          : parsedData;

        setProducts(filteredData);
        // console.log('Products data fetched from cache', filteredData);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const token = generateJWTToken();
        const response = await fetch('https://cqfjxolodeqrlvaifybm.supabase.co/functions/v1/get-products', {
          method: "POST", 
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            platform: "website"
          })
        });
        const result = await response.json();
        // console.log('Products data fetched from API:', result);

        if (result.success) {
          // Filter out products that are for development only if in production
          const isProduction = process.env.NEXT_PUBLIC_IS_PRODUCTION === 'true';
          const filteredProducts = isProduction 
            ? result.data.filter(nft => !nft.is_development) 
            : result.data;

          setProducts(filteredProducts);
          
          // Cache the data and timestamp
          sessionStorage.setItem('productsData', JSON.stringify(filteredProducts));
          sessionStorage.setItem('productsTimestamp', Date.now().toString());
        } else {
          setError(result.error);
          console.error("Error fetching Products:", result.error);
        }
      } catch (error) {
        setError(error.message);
        console.error("Error fetching Products:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    let filtered = [...products];

    // Filter by isActive
    filtered = filtered.filter((product) => product.is_active_all || product.is_active_website);

    // Filter by product names
    if (filters.name) {
      filtered = filtered.filter((product) =>
        product.name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    // Filter by currency
    if (filters.currency !== "ALL") {
      filtered = filtered.filter((product) => 
        product.default_currency?.symbol === filters.currency
      );
    }

    // Filter by rarity
    if (filters.rarity.length > 0) {
      filtered = filtered.filter((product) => filters.rarity.includes(product.rarity));
    }

    // Filter by category
    if (filters.category.length > 0) {
      filtered = filtered.filter((product) => filters.category.includes(product.category));
    }

    // Sort by option
    switch (filters.sort) {
      // case "Popularity":
      //   filtered = filtered.sort((a, b) => b.popularity - a.popularity);
      //   break;
      // case "Relevance":
      //   filtered = filtered.sort((a, b) => b.relevance - a.relevance);
      //   break;
      case "Price: Low to High":
        filtered = filtered.sort(
          (a, b) =>
            (getStoreDisplayPrice(a) ?? 0) - (getStoreDisplayPrice(b) ?? 0)
        );
        break;
      case "Price: High to Low":
        filtered = filtered.sort(
          (a, b) =>
            (getStoreDisplayPrice(b) ?? 0) - (getStoreDisplayPrice(a) ?? 0)
        );
        break;
      case "New Arrivals":
      default:
        filtered = filtered.sort(
          (a, b) => new Date(b.creation_date) - new Date(a.creation_date)
        );
        break;
    }

    setFilteredProducts(filtered);
    // Reset to first page whenever filters change
    setCurrentPage(1);
  }, [products, filters]);

  return (
    <Fragment>
      <StoreVideoBanner />
      <div className="flex flex-col md:flex-row p-8 space-y-6 md:space-y-0 justify-center">
        {isLoading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <p>Loading Products...</p>
          </div>
        ) : error ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <p className="text-red-500">Error: {error}</p>
          </div>
        ) : (
          <>
            <Filters filters={filters} setFilters={setFilters} products={products} />
            <div className="flex flex-col md:ml-12">
              {filteredProducts.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                    {getCurrentPageItems().map((product) => (
                      <div className="grid justify-center" key={product.pid}>
                        <ProductCard product={product} />
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center mt-8 space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded-lg ${
                          currentPage === 1
                            ? 'bg-gray-600 cursor-not-allowed'
                            : 'bg-metaxseed-blue hover:bg-metaxseed-open-blue '
                        } text-white transition-colors`}
                      >
                        Prev
                      </button>
                      
                      <div className="flex space-x-2">
                        {[...Array(totalPages)].map((_, index) => (
                          <button
                            key={index + 1}
                            onClick={() => handlePageChange(index + 1)}
                            className={`w-10 h-10 rounded-lg ${
                              currentPage === index + 1
                                ? 'bg-metaxseed-orange-dark text-white'
                                : 'bg-[#141414] text-gray-400 hover:bg-[#2a2f3b]'
                            } transition-colors`}
                          >
                            {index + 1}
                          </button>
                        ))}
                      </div>
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-4 py-2 rounded-lg ${
                          currentPage === totalPages
                            ? 'bg-gray-600 cursor-not-allowed'
                            : 'bg-metaxseed-blue hover:bg-metaxseed-open-blue'
                        } text-white transition-colors`}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="md:min-w-[928px] min-h-[200px] flex justify-center items-center">
                  <p>No Products available</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Fragment>
  );
};

export default Store;
